import { useLingui } from "@lingui/react/macro";
import dayjs from "dayjs";

/**
 * Labels traducibles para cada feature de límite del plan.
 * El backend envía `label` en es-ES hardcodeado; se traduce en frontend vía `item.feature`.
 * Fallback al `label` del backend si la feature es desconocida.
 *
 * @returns {Record<string, string>}
 */
export function useFeatureLabels() {
	const { t } = useLingui();
	// ponytail: Object.create(null) evita lookups por prototipo (constructor, toString...) en `featureLabels[feature]`
	return Object.assign(Object.create(null), {
		"crew.maxItems": t`Espacios`,
		"crew.totalMembers": t`Miembros`,
		"money.maxLists": t`Listas de dinero`,
		"money.maxItemsPerMonth": t`Movimientos / mes`,
		"items.maxItems": t`Ítems`,
		"items.maxCategories": t`Categorías`,
		"tasks.maxItems": t`Tareas`,
		"recipes.maxItems": t`Recetas`,
	});
}

/**
 * Calcula el porcentaje de uso de un límite.
 * Devuelve un valor entre 0 y 100, redondeado.
 * Si el límite es 0 o negativo devuelve 0 (evita división por cero).
 *
 * @param {number} used
 * @param {number} limit
 * @returns {number}
 */
export function calcUsagePercent(used, limit) {
	if (limit <= 0) return 0;
	return Math.min(100, Math.round((used / limit) * 100));
}

/**
 * Devuelve el color según el porcentaje de uso.
 * 0-70% verde, 70-90% naranja, 90-100% rojo.
 * @param {number} percent
 * @returns {"green" | "orange" | "red"}
 */
export function getUsageColor(percent) {
	if (percent < 70) return "green";
	if (percent < 100) return "orange";
	return "red";
}

/**
 * Normaliza una fecha (Date, string, o undefined) a formato ISO date-only `YYYY-MM-DD`.
 * Si no se pasa `date`, usa la fecha actual.
 *
 * @param {Date | string} [date]
 * @returns {string}
 */
export function toISODate(date) {
	return dayjs(date ?? new Date()).format("YYYY-MM-DD");
}
